import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { writeData } from "../../../api";
import { insertPageBlock } from "../../../api/page";
import { fetchHttpHookApi } from "../../../api/httpHook";
import { ActionType, AppDispatch, RootState } from "../../../reducer";
import { HttpHook } from "../../../types/httpHook";
import { HttpHookBlock } from "../../../types/notebook/note/block/httpHook";
import { useAppIdMap } from "../../app";
import { useSelectedNotebookPageIdMap } from "../../page";
import { useInsertBlock } from "../block";
import { loadKey, loadStatus } from "../../loadStatus";
import { requireRecord, useTrackedLoad } from "../../loadTracking";
import { useLoadError } from "../../../slices/ui";
import { AddHttpHooksAction } from "../../../reducer/httpHook";

export const useAddHttpHooks = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (httpHooks: HttpHook[]) => {
    const action: AddHttpHooksAction = {
      type: ActionType.ADD_HTTP_HOOKS,
      httpHooks,
    };
    dispatch(action as any);
  };
};

export const useHttpHook = (hookId: string | undefined) => {
  const httpHook = useSelector((state: RootState) => {
    if (!hookId) return undefined;
    return state.ui.httpHookMap?.[hookId];
  });
  return { httpHook };
};

export const useHttpHookLoad = (hookId: string | undefined) => {
  const addHttpHooks = useAddHttpHooks();
  const trackLoad = useTrackedLoad();
  const { appId } = useAppIdMap();
  const { httpHook } = useHttpHook(hookId);
  const key = hookId ? loadKey("http-hook", hookId) : undefined;
  const error = useLoadError(key);
  const retry = () =>
    hookId && appId && key
      ? trackLoad(
          key,
          requireRecord(fetchHttpHookApi({ "hook-id": hookId, "app-id": appId }), "HTTP hook").then((hook) => {
            addHttpHooks([hook]);
            return hook;
          }),
        )
      : Promise.resolve(undefined);
  useEffect(() => {
    if (!hookId || !appId) return;
    retry();
  }, [hookId, appId]);
  return { httpHook, status: loadStatus(httpHook, error), retry };
};

export const useFetchHttpHookListener = (hookId: string | undefined) => {
  const addHttpHooks = useAddHttpHooks();
  const { appId } = useAppIdMap();
  useEffect(() => {
    if (!hookId || !appId) return;
    fetchHttpHookApi({ "hook-id": hookId, "app-id": appId }).then((hook) => {
      addHttpHooks([hook]);
    });
  }, [hookId, appId]);
};

export const useInsertHttpHookBlock = () => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { appId } = useAppIdMap();
  const insertBlock = useInsertBlock();
  const addHttpHooks = useAddHttpHooks();

  return async (config: {
    blockIndex: number;
    pageId: string;
    protoName: string;
    methodName: string;
  }) => {
    const { blockIndex, pageId, protoName, methodName } = config;
    const hookId = uuidv4();
    const httpHook: HttpHook = {
      type: "omega/query-omega/http-hook/HttpHook",
      "hook-id": hookId,
      "app-id": appId!,
      "page-id": pageId,
      "proto-name": protoName,
      "method-name": methodName,
    };
    const block: HttpHookBlock = {
      "block-id": uuidv4(),
      "app-id": appId!,
      "hook-id": hookId,
      "page-id": selectedNotebookPageId!,
      type: "omega/query-omega/page/block/http-hook/HttpHookBlock",
    };
    addHttpHooks([httpHook]);
    insertBlock({ block, blockIndex });
    await writeData(httpHook);
    await insertPageBlock({ block, "block-index": blockIndex });
    return block;
  };
};
